import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Calendar, ArrowRight, BookOpen, Search } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import api from '@/services/api'
import { useState } from 'react'

interface BlogPost {
  id: number
  slug: string
  title: string
  excerpt: string | null
  category: string | null
  tags: string | null
  published_at: string | null
  author_name: string | null
  og_image: string | null
}

const CATEGORIES = [
  'All', 'SIM Cards', 'Broadband', 'Mobile', 'Guides', 'News', 'Offers',
]

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const category = searchParams.get('category') || ''
  const [search, setSearch] = useState('')
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading } = useQuery({
    queryKey: ['blog', page, category],
    queryFn: async () => {
      const params: Record<string, string> = { page: String(page), per_page: '9' }
      if (category && category !== 'All') params.category = category
      const res = await api.get('/blog', { params })
      return res.data
    },
  })

  const posts: BlogPost[] = data?.posts || []
  const total: number = data?.total || 0
  const totalPages = Math.ceil(total / 9)

  const filtered = search
    ? posts.filter(
        p =>
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          (p.excerpt || '').toLowerCase().includes(search.toLowerCase()),
      )
    : posts

  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Blog', url: 'https://www.siluxmobile.co.uk/blog' },
  ]

  return (
    <>
      <SEOHead
        title="Silux Mobile Blog | SIM Cards, Broadband & Mobile Tips UK"
        description="Expert guides on cheap SIM cards, mobile broadband, data SIM deals, free SIMs and broadband tips for UK customers. Stay informed with Silux Mobile."
        keywords={['cheap sim cards', 'mobile broadband', 'SIM only deals', 'broadband UK', 'data sim cards', 'free sim card UK']}
        canonical="https://www.siluxmobile.co.uk/blog"
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-primary-600 to-primary-800 text-white text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
            <BookOpen className="w-4 h-4" />
            <span className="text-sm font-medium">Knowledge Centre</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            SIM Cards, Broadband & Mobile Guides
          </h1>
          <p className="text-lg text-white/80 mb-8">
            Expert advice on cheap SIM cards, mobile broadband, data SIM deals and everything you need to stay connected in the UK.
          </p>
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search guides..."
              className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 px-4 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSearchParams(cat === 'All' ? {} : { category: cat })}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                (cat === 'All' && !category) || cat === category
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No articles found. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(post => (
                <article
                  key={post.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 overflow-hidden group"
                >
                  {post.og_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.og_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {post.category && (
                        <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                          {post.category}
                        </span>
                      )}
                      {post.published_at && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.published_at).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    )}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all"
                    >
                      Read more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSearchParams({ page: String(i + 1), ...(category ? { category } : {}) })}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    page === i + 1
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Internal Cross-links */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore Our Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Data SIM Cards', to: '/data-sim-cards' },
              { label: 'Rolling Monthly Broadband', to: '/rolling-monthly-broadband' },
              { label: 'Cheap Broadband', to: '/cheap-broadband' },
              { label: 'Mobile Broadband', to: '/mobile-broadband' },
              { label: 'Free SIM Card', to: '/sim-only' },
              { label: 'SIM Only Deals', to: '/sim-only' },
              { label: 'Network Coverage', to: '/network-coverage' },
              { label: 'Roaming SIMs', to: '/roaming' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="block p-3 bg-white rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-600 transition-all"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
